import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { Badge } from '../atoms';
import { DataTable } from './DataTable';
import { formatDate } from '../lib/orgTimezone';

/**
 * CollectorStatementsPanel — the installment statements a COLLECTOR issues, on the
 * collector's own profile (W0 · A0, criterion 5).
 *
 * WHY THIS EXISTS: `tax_statements` is keyed
 * `UNIQUE(collector_id, process_year, statement_number)` — it has been
 * collector-scoped since the table shipped. But it had no frontend surface at all,
 * so the only place a statement was visible was in tax-bill (i.e. client) context.
 * A person looking for "which statements does this collector issue" had nowhere to
 * look, and looked under the client. The data model was already right; the read
 * surface was missing.
 *
 * A statement is the collector's SCHEDULE — "1st Statement, due 30 Nov, 50%" — not
 * a per-parcel amount. The per-bill split lives in `tax_bill_installments`, keyed to
 * the bill, and belongs on the bill rather than here.
 *
 * Read-only. Statements arrive from the collector, and nothing in the demo asked to
 * author them in-app; an edit path can be added if that turns out to be wanted.
 *   list → GET /api/tax-statements?collector_id=<id>   (generic CRUD FK filter)
 */

interface TaxStatement {
  id: string;
  process_year: number | null;
  tax_year: number | null;
  statement_number: number | null;
  statement_label: string | null;
  due_date: string | null;
  delinquent_date: string | null;
  percent: number | string | null;
  discount_available: boolean | null;
}

export interface CollectorStatementsPanelProps {
  collectorId: string;
}

/** Percent is NUMERIC and nullable — absent is not 0, so render the dash. */
function fmtPercent(v: number | string | null): string {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${n}%` : '—';
}

export function CollectorStatementsPanel({ collectorId }: CollectorStatementsPanelProps) {
  const q = useQuery<TaxStatement[]>({
    queryKey: ['tax-statements', 'collector_id', collectorId],
    queryFn: ({ signal }) => api.get<TaxStatement[]>(`/api/tax-statements?collector_id=${collectorId}`, signal),
    enabled: !!collectorId,
  });

  const columns = useMemo<ColumnDef<TaxStatement>[]>(() => [
    {
      accessorKey: 'statement_number',
      header: '#',
      cell: ({ getValue }) => (getValue() as number | null) ?? '—',
    },
    {
      accessorKey: 'statement_label',
      header: 'Statement',
      cell: ({ getValue }) => (getValue() as string | null) || '—',
    },
    { accessorKey: 'tax_year', header: 'Tax Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'process_year', header: 'Process Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'percent', header: 'Percent', cell: ({ getValue }) => fmtPercent(getValue() as number | string | null) },
    {
      accessorKey: 'due_date',
      header: 'Due',
      cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }),
    },
    {
      accessorKey: 'delinquent_date',
      header: 'Delinquent',
      cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }),
    },
    {
      accessorKey: 'discount_available',
      header: 'Discount',
      cell: ({ getValue }) => (getValue() ? <Badge variant="success">Available</Badge> : '—'),
    },
  ], []);

  const rows = q.data ?? [];

  if (q.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading statements…</p>;
  }
  if (q.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>{(q.error as Error).message}</div>
    );
  }
  if (rows.length === 0) {
    return (
      <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        No installment statements recorded for this collector.
      </p>
    );
  }
  return <DataTable data={rows} columns={columns} />;
}
